import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import apiClient from "../../core/config/api";
import type { AppDispatch } from "../../app/store";
import {
    disconnectHubspot,
    extractErrorMessage,
    fetchHubspotConnection,
    fetchHubspotExecutions,
    selectHubspotConnecting,
    selectHubspotConnection,
    selectHubspotConnectionLoaded,
    selectHubspotDisconnecting,
    selectHubspotError,
    selectHubspotExecutions,
    startHubspotConnect,
} from "../../features/integrations/store/hubspotSlice";
import HubspotListVerifier from "../../features/integrations/components/HubspotListVerifier";
import HubspotDisconnectModal from "../../features/integrations/components/HubspotDisconnectModal";
import SalesforceListVerifier from "../../features/integrations/components/SalesforceListVerifier";
import type { SalesforceConnection } from "../../features/integrations/types";
import { HubspotLogo, SalesforceLogo } from "./integrationLogos";
import "./integration.css";
// Pagination styles, shared with Verification Logs.
import "./email_verify.css";

const LOG_PAGE_SIZE = 10;

// The backend's OAuth callback redirects back here with ?hubspot=connected or
// ?hubspot=error, because the browser leaves the SPA entirely for HubSpot's
// consent screen and comes back with no in-memory state to report the outcome.
// Keys MUST match the reason codes emitted by the backend callback in
// app/api/v1/endpoints/hubspot.py. An unmapped code falls back to generic copy,
// which hides the actual cause - so keep the two lists in step.
const OAUTH_ERROR_COPY: Record<string, string> = {
    portal_taken:
        "That HubSpot portal is already connected to a different ReachStream account. Disconnect it there first, then try again.",
    invalid_callback: "HubSpot sent back an incomplete response. Please try connecting again.",
    expired_link: "That connection link expired. Please start again.",
    access_denied: "The HubSpot authorization was cancelled, so nothing was connected.",
    not_configured: "The HubSpot integration is not configured on this environment yet.",
    connect_failed:
        "We reached HubSpot but could not finish setting up the connection. Please try again, and contact support if it keeps happening.",
};

// Same contract as above, for the reason codes in app/api/v1/endpoints/salesforce.py.
const SALESFORCE_ERROR_COPY: Record<string, string> = {
    org_taken:
        "That Salesforce org is already connected to a different ReachStream account. Disconnect it there first, then try again.",
    api_disabled:
        "Your Salesforce edition or user has no API access. Enterprise, Unlimited, Performance and Developer editions include it; Professional needs Salesforce's API add-on.",
    invalid_callback: "Salesforce sent back an incomplete response. Please try connecting again.",
    expired_link: "That connection link expired. Please start again.",
    access_denied: "The Salesforce authorization was cancelled, so nothing was connected.",
    app_not_installed:
        "Salesforce blocked the sign-in for this org. Check you signed in to the right Salesforce org, or ask your Salesforce admin to install the ReachStream app first.",
    not_configured: "The Salesforce integration is not configured on this environment yet.",
    connect_failed:
        "We reached Salesforce but could not finish setting up the connection. Please try again, and contact support if it keeps happening.",
};

// "Notify Me" on a coming-soon card is remembered in this browser so the button
// stays "We'll notify you"; the request itself is recorded server-side.
const NOTIFY_STORAGE_KEY = "integration-notify-me";

const readNotified = (): string[] => {
    try {
        const raw = window.localStorage.getItem(NOTIFY_STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

const Integration = () => {
    const [notified, setNotified] = useState<string[]>(readNotified);
    const [notifying, setNotifying] = useState<string | null>(null);

    const dispatch = useDispatch<AppDispatch>();
    const [searchParams, setSearchParams] = useSearchParams();
    const hubspotConnection = useSelector(selectHubspotConnection);
    const hubspotConnectionLoaded = useSelector(selectHubspotConnectionLoaded);
    const hubspotExecutions = useSelector(selectHubspotExecutions);
    const hubspotConnecting = useSelector(selectHubspotConnecting);
    const hubspotDisconnecting = useSelector(selectHubspotDisconnecting);
    const hubspotError = useSelector(selectHubspotError);
    const [confirmDisconnect, setConfirmDisconnect] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [goToPageInput, setGoToPageInput] = useState("");

    // "unavailable" keeps the Coming soon card: the backend answers 503 while
    // SALESFORCE_INTEGRATION_ENABLED is off, and 404 before it has the endpoint.
    const [salesforceState, setSalesforceState] = useState<"loading" | "unavailable" | "ready">("loading");
    const [salesforceConnection, setSalesforceConnection] = useState<SalesforceConnection | null>(null);
    const [salesforceConnecting, setSalesforceConnecting] = useState(false);
    const [salesforceDisconnecting, setSalesforceDisconnecting] = useState(false);
    const [salesforceSyncing, setSalesforceSyncing] = useState(false);
    const [confirmSalesforceDisconnect, setConfirmSalesforceDisconnect] = useState(false);

    const hubspotConnected = hubspotConnection?.status === "active";
    const salesforceLabel = salesforceConnection?.org_name || salesforceConnection?.org_id || "";

    const loadSalesforceConnection = useCallback(async () => {
        try {
            const { data } = await apiClient.get<SalesforceConnection | null>("/salesforce/connection");
            setSalesforceConnection(data || null);
            setSalesforceState("ready");
        } catch {
            setSalesforceState("unavailable");
        }
    }, []);

    useEffect(() => {
        dispatch(fetchHubspotConnection());
        loadSalesforceConnection();
    }, [dispatch, loadSalesforceConnection]);

    // Pull the verification log only once a portal is actually connected -
    // before that the endpoint has nothing to return. 100 is the endpoint's
    // maximum; the table pages through it client-side.
    useEffect(() => {
        if (hubspotConnected) {
            dispatch(fetchHubspotExecutions({ limit: 100 }));
        }
    }, [dispatch, hubspotConnected]);

    useEffect(() => {
        const result = searchParams.get("hubspot");
        if (!result) return;

        // The backend signals success with hubspot=connected (NOT "success").
        if (result === "connected") {
            const incomplete = searchParams.get("warning") === "setup_incomplete";
            if (incomplete) {
                toast.error(
                    "HubSpot is connected, but we could not finish setting up the ReachStream contact properties. Use Sync properties to retry."
                );
            } else {
                toast.success("HubSpot connected successfully.");
            }
            dispatch(fetchHubspotConnection());
        } else {
            const reason = searchParams.get("reason") || "";
            const detail = searchParams.get("detail");
            const base =
                OAUTH_ERROR_COPY[reason] ||
                "We could not complete the HubSpot connection. Please try again.";
            // The backend sends a short, non-sensitive failure summary. Showing it
            // beats "try again" when the person hitting this cannot read app logs.
            toast.error(detail ? `${base} (${detail})` : base);
        }

        // Strip the params so a refresh doesn't replay the banner.
        const next = new URLSearchParams(searchParams);
        next.delete("hubspot");
        next.delete("reason");
        next.delete("portal_id");
        next.delete("warning");
        next.delete("detail");
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams, dispatch]);

    useEffect(() => {
        const result = searchParams.get("salesforce");
        if (!result) return;

        if (result === "connected") {
            if (searchParams.get("warning") === "setup_incomplete") {
                toast.error(
                    "Salesforce is connected, but we could not finish setting up the ReachStream fields. Use Retry setup on the Salesforce card."
                );
            } else {
                toast.success("Salesforce connected successfully.");
            }
            loadSalesforceConnection();
        } else {
            const reason = searchParams.get("reason") || "";
            const detail = searchParams.get("detail");
            const base =
                SALESFORCE_ERROR_COPY[reason] ||
                "We could not complete the Salesforce connection. Please try again.";
            toast.error(detail ? `${base} (${detail})` : base);
        }

        const next = new URLSearchParams(searchParams);
        next.delete("salesforce");
        next.delete("reason");
        next.delete("warning");
        next.delete("detail");
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams, loadSalesforceConnection]);

    const handleHubspotConnect = async () => {
        const action = await dispatch(startHubspotConnect());
        if (startHubspotConnect.fulfilled.match(action)) {
            // Full page navigation, not a router push: the consent screen is on
            // HubSpot's origin.
            window.location.href = action.payload.authorize_url;
        }
    };

    const handleHubspotDisconnect = async () => {
        const action = await dispatch(disconnectHubspot());
        if (disconnectHubspot.fulfilled.match(action)) {
            setConfirmDisconnect(false);
            toast.success("HubSpot disconnected.");
            return;
        }
        // The thunk uses rejectWithValue, so it resolves either way. Close the
        // popup and let the inline error under the cards carry the reason.
        setConfirmDisconnect(false);
        toast.error(action.payload || "Could not disconnect the HubSpot portal");
    };

    // Sandbox orgs sign in at test.salesforce.com, so the choice is made here,
    // before the browser leaves for Salesforce.
    const handleSalesforceConnect = async (environment: "production" | "sandbox") => {
        setSalesforceConnecting(true);
        try {
            const { data } = await apiClient.post<{ authorize_url: string }>("/salesforce/connect", {
                environment,
            });
            window.location.href = data.authorize_url;
        } catch (err: unknown) {
            toast.error(extractErrorMessage(err, "Could not start the Salesforce connection. Please try again."));
            setSalesforceConnecting(false);
        }
    };

    const handleSalesforceRetrySetup = async () => {
        setSalesforceSyncing(true);
        try {
            await apiClient.post("/salesforce/fields/sync");
            toast.success("Salesforce setup finished.");
        } catch (err: unknown) {
            toast.error(extractErrorMessage(err, "Could not finish the Salesforce setup. Please try again."));
        } finally {
            setSalesforceSyncing(false);
            loadSalesforceConnection();
        }
    };

    const handleSalesforceDisconnect = async () => {
        setSalesforceDisconnecting(true);
        try {
            await apiClient.delete("/salesforce/connection");
            setSalesforceConnection(null);
            toast.success("Salesforce disconnected.");
        } catch (err: unknown) {
            toast.error(extractErrorMessage(err, "Could not disconnect the Salesforce org"));
        } finally {
            setSalesforceDisconnecting(false);
            setConfirmSalesforceDisconnect(false);
        }
    };

    const handleNotifyMe = async (integration: string, label: string) => {
        if (notifying || notified.includes(integration)) return;
        setNotifying(integration);
        try {
            await apiClient.post("/account/re/integration-interest", { integration });
            const next = [...notified, integration];
            setNotified(next);
            try {
                window.localStorage.setItem(NOTIFY_STORAGE_KEY, JSON.stringify(next));
            } catch {
                // Only the button label depends on it.
            }
            toast.success(`Thanks! We'll let you know when ${label} is available.`);
        } catch (err: unknown) {
            toast.error(extractErrorMessage(err, "Something went wrong. Please try again."));
        } finally {
            setNotifying(null);
        }
    };

    // GET /hubspot/executions returns no total, so the loaded batch is paged
    // client-side. A refetch can leave fewer pages than the one being viewed.
    const totalPages = Math.max(1, Math.ceil(hubspotExecutions.length / LOG_PAGE_SIZE));
    const page = Math.min(currentPage, totalPages);
    const pagedExecutions = hubspotExecutions.slice((page - 1) * LOG_PAGE_SIZE, page * LOG_PAGE_SIZE);

    const handlePageChange = (next: number) => {
        if (next >= 1 && next <= totalPages) {
            setCurrentPage(next);
        }
    };

    const handleGoToPage = () => {
        const next = parseInt(goToPageInput);
        if (!isNaN(next) && next >= 1 && next <= totalPages) {
            handlePageChange(next);
            setGoToPageInput(String(next));
        }
    };

    const renderPageNumbers = () => {
        const pages = [] as (number | string)[];
        const maxVisiblePages = 5;

        if (totalPages <= maxVisiblePages) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else if (page <= 3) {
            pages.push(1, 2, 3, "...", totalPages);
        } else if (page >= totalPages - 2) {
            pages.push(1, "...", totalPages - 2, totalPages - 1, totalPages);
        } else {
            pages.push(1, "...", page, "...", totalPages);
        }

        return pages;
    };

    return (
        <div className="integration-page">
            <h1>Integrations</h1>
            <p className="integration-subtitle">
                Connect ReachStream Email Verifier with your favorite CRM and marketing platforms.
            </p>

            <div className="integration-section">
                <div className="section-heading">
                    <span className="section-icon-box">
                        <img src="/images/integration-active.png" alt="" />
                    </span>
                    <span>CRM &amp; Marketing Platforms</span>
                </div>

                <div className="platforms-card">
                    <div className="platform-card">
                        <div className="platform-card-head">
                            <span className="platform-logo">
                                <HubspotLogo />
                            </span>
                            <span className="platform-name">Connect HubSpot</span>
                            <span className="platform-badge is-live">{hubspotConnected ? "Connected" : "Live"}</span>
                        </div>

                        {!hubspotConnectionLoaded ? (
                            <div className="integration-loading" role="status" aria-live="polite">
                                <span className="integration-skeleton-line" />
                                <span className="integration-skeleton-line is-short" />
                                <span className="integration-skeleton-btn" />
                                <span className="visually-hidden">Checking your HubSpot connection</span>
                            </div>
                        ) : hubspotConnected ? (
                            <>
                                <p className="platform-desc">
                                    Connected to{" "}
                                    <strong>{hubspotConnection?.hub_domain || `Portal ${hubspotConnection?.portal_id}`}</strong>.
                                    Verify a whole contact list below, or add the{" "}
                                    <strong>Verify Email with ReachStream</strong> step to a HubSpot workflow to
                                    verify contacts as they move through it. Workflows need HubSpot
                                    Professional or Enterprise.
                                </p>
                                <button
                                    type="button"
                                    className="platform-btn is-outline"
                                    onClick={() => setConfirmDisconnect(true)}
                                >
                                    Disconnect
                                </button>
                            </>
                        ) : (
                            <>
                                <p className="platform-desc">
                                    Sync ReachStream with HubSpot to enrich and update your CRM data in real time.
                                </p>
                                <button
                                    type="button"
                                    className="platform-btn"
                                    onClick={handleHubspotConnect}
                                    disabled={hubspotConnecting}
                                >
                                    {hubspotConnecting ? "Redirecting..." : "Connect to HubSpot"}
                                </button>
                            </>
                        )}
                    </div>

                    <div className="platform-card">
                        <div className="platform-card-head">
                            <span className="platform-logo">
                                <SalesforceLogo />
                            </span>
                            <span className="platform-name">Connect Salesforce</span>
                            {salesforceState === "unavailable" && <span className="platform-badge">Coming soon</span>}
                            {salesforceState === "ready" && (
                                <span className="platform-badge is-live">{salesforceConnection ? "Connected" : "Live"}</span>
                            )}
                        </div>

                        {salesforceState === "loading" ? (
                            <div className="integration-loading" role="status" aria-live="polite">
                                <span className="integration-skeleton-line" />
                                <span className="integration-skeleton-line is-short" />
                                <span className="integration-skeleton-btn" />
                                <span className="visually-hidden">Checking your Salesforce connection</span>
                            </div>
                        ) : salesforceState === "unavailable" ? (
                            <>
                                <p className="platform-desc">
                                    Sync ReachStream with Salesforce to enrich and update your CRM data in real time.
                                </p>
                                <button
                                    type="button"
                                    className="platform-btn is-outline"
                                    onClick={() => handleNotifyMe("salesforce", "Salesforce")}
                                    disabled={notifying === "salesforce" || notified.includes("salesforce")}
                                >
                                    {notified.includes("salesforce")
                                        ? "We'll notify you"
                                        : notifying === "salesforce"
                                            ? "Saving..."
                                            : "Notify Me"}
                                </button>
                            </>
                        ) : salesforceConnection ? (
                            <>
                                <p className="platform-desc">
                                    Connected to <strong>{salesforceLabel}</strong>
                                    {salesforceConnection.is_sandbox && " (sandbox)"}
                                    {salesforceConnection.sf_username && <> as {salesforceConnection.sf_username}</>}.
                                    {salesforceConnection.last_error && (
                                        <span className="platform-desc-error">{salesforceConnection.last_error}</span>
                                    )}
                                </p>
                                <div className="platform-actions">
                                    {salesforceConnection.last_error && (
                                        <button
                                            type="button"
                                            className="platform-btn"
                                            onClick={handleSalesforceRetrySetup}
                                            disabled={salesforceSyncing}
                                        >
                                            {salesforceSyncing ? "Retrying..." : "Retry setup"}
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        className="platform-btn is-outline"
                                        onClick={() => setConfirmSalesforceDisconnect(true)}
                                    >
                                        Disconnect
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <p className="platform-desc">
                                    Verify the emails on your Salesforce leads and contacts. Needs a Salesforce
                                    edition with API access, such as Enterprise, Unlimited or Developer.
                                </p>
                                <button
                                    type="button"
                                    className="platform-btn"
                                    onClick={() => handleSalesforceConnect("production")}
                                    disabled={salesforceConnecting}
                                >
                                    {salesforceConnecting ? "Redirecting..." : "Connect to Salesforce"}
                                </button>
                                {/* <button
                                    type="button"
                                    className="platform-alt-link"
                                    onClick={() => handleSalesforceConnect("sandbox")}
                                    disabled={salesforceConnecting}
                                >
                                    Connecting a sandbox org?
                                </button> */}
                            </>
                        )}
                    </div>
                </div>

                {hubspotError && <p className="integration-inline-error">{hubspotError}</p>}

                {hubspotConnected && (
                    <>
                        <HubspotListVerifier />

                        {hubspotExecutions.length > 0 && (
                            <div className="hubspot-log">
                                <div className="hubspot-log-head">Recent HubSpot verifications</div>
                                <table className="hubspot-log-table">
                                    <thead>
                                        <tr>
                                            <th>Email</th>
                                            <th>Result</th>
                                            <th>Written to HubSpot</th>
                                            <th>When</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pagedExecutions.map((row) => {
                                            const verdict = (row.verification_status || "").toLowerCase();
                                            return (
                                                <tr key={row.id}>
                                                    <td>{row.email || <span className="muted">No email on contact</span>}</td>
                                                    <td>
                                                        {verdict ? (
                                                            <span className={`verdict-badge ${verdict}`}>{verdict.replace(/_/g, " ")}</span>
                                                        ) : (
                                                            <span className="muted">Pending</span>
                                                        )}
                                                    </td>
                                                    <td className="muted">{row.writeback_status}</td>
                                                    <td className="muted">{new Date(row.created_at).toLocaleString()}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                                {totalPages > 1 && (
                                    <div className="pagination-container">
                                        <button
                                            className="pagination-arrow"
                                            onClick={() => handlePageChange(page - 1)}
                                            disabled={page === 1}
                                        >
                                            &lt;
                                        </button>

                                        {renderPageNumbers().map((item, index) => (
                                            item === "..." ? (
                                                <span key={`ellipsis-${index}`} className="pagination-ellipsis">...</span>
                                            ) : (
                                                <button
                                                    key={item}
                                                    className={`pagination-number ${page === item ? "active" : ""}`}
                                                    onClick={() => handlePageChange(item as number)}
                                                >
                                                    {item}
                                                </button>
                                            )
                                        ))}
                                        <button
                                            className="pagination-arrow"
                                            onClick={() => handlePageChange(page + 1)}
                                            disabled={page === totalPages}
                                        >
                                            &gt;
                                        </button>

                                        <div className="go-to-page">
                                            <span>Go to page</span>
                                            <input
                                                type="number"
                                                min={1}
                                                max={totalPages}
                                                value={goToPageInput}
                                                onChange={(e) => setGoToPageInput(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") {
                                                        handleGoToPage();
                                                    }
                                                }}
                                                onBlur={handleGoToPage}
                                                className="page-input"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}

                {salesforceState === "ready" && salesforceConnection && <SalesforceListVerifier />}
            </div>

            <HubspotDisconnectModal
                isOpen={confirmDisconnect}
                portalLabel={hubspotConnection?.hub_domain || `Portal ${hubspotConnection?.portal_id}`}
                isDisconnecting={hubspotDisconnecting}
                onClose={() => setConfirmDisconnect(false)}
                onConfirm={handleHubspotDisconnect}
            />

            <HubspotDisconnectModal
                isOpen={confirmSalesforceDisconnect}
                platformName="Salesforce"
                portalLabel={salesforceLabel}
                isDisconnecting={salesforceDisconnecting}
                onClose={() => setConfirmSalesforceDisconnect(false)}
                onConfirm={handleSalesforceDisconnect}
            />
        </div>
    );
};

export default Integration;
